import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import miLogo from './assets/mi-logo.png';

const formatCurrency = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00 €';
    return number.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const safeText = (text) => (text ? String(text) : '---');

const styles = StyleSheet.create({
    page: { fontFamily: 'Helvetica', fontSize: 10, paddingTop: 40, paddingBottom: 80, paddingLeft: 40, paddingRight: 40, color: '#232559', backgroundColor: '#ffffff' },
    header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 15, borderBottomWidth: 2, borderBottomColor: '#1BB1A5' },
    
    headerLeft: { width: '50%', alignItems: 'flex-start' },
    logo: { width: 140, marginBottom: 10 }, 
    companyInfo: { fontSize: 10, color: '#555', marginTop: 2 },
    
    headerRight: { width: '50%', alignItems: 'flex-end', paddingTop: 10 },
    docTitle: { fontSize: 18, fontWeight: 'bold', color: '#232559', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 },
    docInfo: { fontSize: 10, color: '#888', marginTop: 2 },
    
    clientSection: { marginTop: 10, marginBottom: 20, padding: 12, backgroundColor: '#f9fafb', borderRadius: 8, borderLeftWidth: 4, borderLeftColor: '#E15A42' },
    sectionTitle: { fontSize: 11, fontWeight: 'bold', marginBottom: 8, color: '#232559', textTransform: 'uppercase', letterSpacing: 1 },
    clientText: { fontSize: 10, marginBottom: 3, color: '#444' },
    
    table: { width: '100%', borderRadius: 8, overflow: 'hidden', marginBottom: 20 },
    tableHeader: { flexDirection: 'row', backgroundColor: '#1BB1A5', padding: 8 },
    tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eee', padding: 8, alignItems: 'center' },
    
    colDesc: { width: '60%', paddingRight: 8 }, 
    colCant: { width: '15%', textAlign: 'center' },
    colTotal: { width: '25%', textAlign: 'right' },
    
    tableCellHeader: { fontSize: 9, fontWeight: 'bold', color: '#ffffff', textTransform: 'uppercase', letterSpacing: 1 },
    tableCell: { fontSize: 9, color: '#333' },

    bloqueTitleContainer: { backgroundColor: '#f0fdfa', padding: 8, borderBottomWidth: 1, borderBottomColor: '#ccfbf1' },
    bloqueTitle: { fontSize: 10, fontWeight: 'bold', color: '#1BB1A5', textTransform: 'uppercase' },
    bloqueDesc: { fontSize: 8, color: '#666', marginTop: 3, fontStyle: 'italic' },

    footerSection: { marginTop: 10, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
    
    condicionesBox: { width: '50%', paddingRight: 15 },
    condicionesTitle: { fontSize: 10, fontWeight: 'bold', color: '#E15A42', marginBottom: 8, textTransform: 'uppercase' },
    condicionesText: { fontSize: 8, color: '#555', marginBottom: 3, lineHeight: 1.4 },
    
    summaryBox: { width: '50%', backgroundColor: '#f9fafb', padding: 10, borderRadius: 8, borderTopWidth: 4, borderTopColor: '#232559' },
    summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
    summaryLabel: { color: '#555', fontSize: 9 },
    summaryValue: { fontWeight: 'bold', fontSize: 9, color: '#232559' },
    totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#ddd' },
    totalLabel: { fontWeight: 'bold', fontSize: 12, color: '#232559' },
    totalValue: { fontWeight: 'bold', fontSize: 12, color: '#1BB1A5' },
    
    footer: { position: 'absolute', bottom: 30, left: 0, right: 0, alignItems: 'center' },
    footerText: { fontSize: 10, fontWeight: 'bold', color: '#1BB1A5', textTransform: 'uppercase', letterSpacing: 2 }
});

const PresupuestoPDF_V2 = ({ presupuesto }) => {
    if (!presupuesto || Object.keys(presupuesto).length === 0) {
        return <Document><Page size="A4" style={styles.page}><Text>Cargando cotización...</Text></Page></Document>;
    }

    const fechaFormateada = presupuesto.fecha || presupuesto.fechaPresupuesto ? new Date(presupuesto.fecha || presupuesto.fechaPresupuesto).toLocaleDateString('es-ES') : '---';
    const c = presupuesto.cliente || presupuesto.datosCliente || {};
    const bloquesData = presupuesto.bloquesPresupuesto || presupuesto.bloques || [];
    const tieneBloques = Array.isArray(bloquesData) && bloquesData.length > 0;

    const totalVenta = parseFloat(presupuesto.totales?.totalGeneral || presupuesto.calculos?.totalGeneral || 0);
    const base21 = parseFloat(presupuesto.totales?.base21 || presupuesto.calculos?.totales?.base21 || 0);
    const base10 = parseFloat(presupuesto.totales?.base10 || presupuesto.calculos?.totales?.base10 || 0);
    const iva21 = parseFloat(presupuesto.totales?.iva21 || presupuesto.calculos?.totales?.iva21 || 0);
    const iva10 = parseFloat(presupuesto.totales?.iva10 || presupuesto.calculos?.totales?.iva10 || 0);
    const descuentoImporte = parseFloat(presupuesto.calculos?.descuentoImporte || 0);
    const subtotalBruto = parseFloat(presupuesto.totales?.subtotalParaPDF || presupuesto.calculos?.subtotal || 0);

    const condicionesArray = Array.isArray(presupuesto.condicionesSeleccionadas) ? presupuesto.condicionesSeleccionadas : [];

    return (
        <Document>
            <Page size="A4" style={styles.page} wrap={true}>
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        {miLogo ? <Image style={styles.logo} src={miLogo} /> : null}
                        <Text style={styles.companyInfo}>Diversión sin límites</Text>
                        <Text style={styles.companyInfo}>Tel: +34 600 000 000</Text>
                        <Text style={styles.companyInfo}>hola@kokimiko.com</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.docTitle}>COTIZACIÓN</Text>
                        <Text style={styles.docInfo}>{`Ref: ${safeText(presupuesto.numeroPresupuesto || presupuesto.numero)}`}</Text>
                        <Text style={styles.docInfo}>{`Fecha: ${fechaFormateada}`}</Text>
                    </View>
                </View>

                <View style={styles.clientSection}>
                    <Text style={styles.sectionTitle}>DATOS DEL CLIENTE</Text>
                    <Text style={styles.clientText}>{safeText(c.name || c.nombreRepresentante)}</Text>
                    {c.telefono ? <Text style={styles.clientText}>{`Teléfono: ${safeText(c.telefono)}`}</Text> : null}
                    {c.email ? <Text style={styles.clientText}>{`Email: ${safeText(c.email)}`}</Text> : null}
                    {c.nif || c.dni ? <Text style={styles.clientText}>{`DNI/CIF: ${safeText(c.nif || c.dni)}`}</Text> : null}
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader} fixed>
                        <View style={styles.colDesc}><Text style={{...styles.tableCellHeader, textAlign: 'left'}}>Servicio / Concepto</Text></View>
                        <View style={styles.colCant}><Text style={styles.tableCellHeader}>Cant.</Text></View>
                        <View style={styles.colTotal}><Text style={{...styles.tableCellHeader, textAlign: 'right'}}>Total</Text></View>
                    </View>

                    {tieneBloques ? (
                        bloquesData.map((bloque, index) => {
                            const subtotalBloque = (bloque.lineas || []).reduce((acc, l) => acc + (parseFloat(l.total || l.costo * l.cantidad) || 0), 0);
                            return (
                                <View key={`block-${index}`} wrap={false}>
                                    <View style={styles.bloqueTitleContainer}>
                                        <Text style={styles.bloqueTitle}>{safeText(bloque.nombre || `Sección ${index + 1}`)}</Text>
                                        {bloque.descripcion ? <Text style={styles.bloqueDesc}>{bloque.descripcion}</Text> : null}
                                    </View>
                                    {(bloque.lineas || []).map((line, lIndex) => (
                                        <View style={styles.tableRow} key={`line-${index}-${lIndex}`}>
                                            <View style={styles.colDesc}>
                                                <Text style={styles.tableCell}>{safeText(line.descripcion || line.producto)}</Text>
                                            </View>
                                            <View style={styles.colCant}><Text style={styles.tableCell}>{String(line.cantidad || "1")}</Text></View>
                                            <View style={styles.colTotal}><Text style={styles.tableCell}>{formatCurrency(line.total || (line.costo * line.cantidad))}</Text></View>
                                        </View>
                                    ))}
                                    <View style={{...styles.tableRow, backgroundColor: '#fafafa'}}>
                                        <View style={{width: '75%', textAlign: 'right', paddingRight: 10}}><Text style={{fontSize: 9, fontWeight: 'bold', color: '#888'}}>Subtotal {safeText(bloque.nombre)}:</Text></View>
                                        <View style={styles.colTotal}><Text style={{fontSize: 9, fontWeight: 'bold', color: '#1BB1A5'}}>{formatCurrency(subtotalBloque)}</Text></View>
                                    </View>
                                </View>
                            );
                        })
                    ) : (
                        <View style={styles.tableRow}><Text style={{fontSize: 9, fontStyle: 'italic', padding: 10, color: '#888'}}>No hay servicios añadidos.</Text></View>
                    )}
                </View>

                <View style={styles.footerSection} wrap={false}>
                    <View style={styles.condicionesBox}>
                        <Text style={styles.condicionesTitle}>Condiciones del Evento</Text>
                        {condicionesArray.length > 0 ? (
                            condicionesArray.map((cond, i) => (
                                <Text key={`cond-${i}`} style={styles.condicionesText}>{`• ${cond}`}</Text>
                            ))
                        ) : (
                            <Text style={styles.condicionesText}>Presupuesto sujeto a disponibilidad de fecha al momento de la reserva.</Text>
                        )}
                    </View>

                    <View style={styles.summaryBox}>
                        <View style={styles.summaryRow}>
                            <Text style={styles.summaryLabel}>Subtotal Bruto:</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(subtotalBruto)}</Text>
                        </View>
                        
                        {descuentoImporte > 0 && (
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>Descuento Comercial:</Text>
                                <Text style={{...styles.summaryValue, color: '#E15A42'}}>- {formatCurrency(descuentoImporte)}</Text>
                            </View>
                        )}

                        {base21 > 0 && (
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>Base Imponible (21%):</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(base21)}</Text>
                            </View>
                        )}
                        
                        {base10 > 0 && (
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>Base Imponible (10%):</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(base10)}</Text>
                            </View>
                        )}

                        {iva21 > 0 && (
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>IVA Servicios (21%):</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(iva21)}</Text>
                            </View>
                        )}
                        
                        {iva10 > 0 && (
                            <View style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>IVA Alimentos (10%):</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(iva10)}</Text>
                            </View>
                        )}

                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>TOTAL:</Text>
                            <Text style={styles.totalValue}>{formatCurrency(totalVenta)}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>¡GRACIAS POR DEJAR LA DIVERSIÓN EN NUESTRAS MANOS!</Text>
                </View>
            </Page>
        </Document>
    );
};

export default PresupuestoPDF_V2;